import {
    requiredTranslatableZodSchema,
    toTranslatableString,
} from "@@/shared/translations/translation";
import { z } from "zod";
import type { ProductFormFieldId } from "../product-form-field-id";
import {
    productFormFieldIdFactory,
    productFormFieldIdZodSchema,
} from "../product-form-field-id";
import { productFormFieldGroupByZodUnion } from "../product-form-field-quantity";

const _productFormTextAnswerZodObject = z.object({
    _id: productFormFieldIdZodSchema,
    _type: z.literal("PRODUCT_FORM_FIELD_ANSWER"),
    type: z.literal("TEXT"),
    title: requiredTranslatableZodSchema,
    required: z.boolean(),
    quantity: z.number().int().positive().min(1),
    values: z.record(z.coerce.number(), z.string()),
    groupedBy: productFormFieldGroupByZodUnion,
});

const productFormTextAnswerZodObject = _productFormTextAnswerZodObject
    .refine(
        data => {
            if (!data.required) return true;
            if (data.quantity !== Object.keys(data.values).length) return false;
            return true;
        },
        data => ({
            message: toTranslatableString({
                sv: "Antalet svar stämmer inte överens med antalet textrutor.",
                en: "The number of answers does not match the number of text fields.",
            }),
            params: {
                quantity: data.quantity,
                values: data.values,
            },
            path: ["values"],
        }),
    )
    .refine(
        data => {
            if (!data.required) return true;
            if (Object.values(data.values).some(item => !item?.trim()))
                return false;
            return true;
        },
        data => ({
            message: toTranslatableString({
                sv: "En obligatorisk textinmatning får inte vara tom.",
                en: "A required text field must not be empty.",
            }),
            params: {
                required: data.required,
                values: data.values,
            },
            path: ["values"],
        }),
    );

type ProductFormField_TextAnswer = z.infer<
    typeof productFormTextAnswerZodObject
>;

const _factoryParamsZodObject = _productFormTextAnswerZodObject.omit({
    _id: true,
    _type: true,
    type: true,
});

type FactoryParams = z.infer<typeof _factoryParamsZodObject>;
const productFormTextAnswerFactory = (
    input: FactoryParams & {
        _id?: ProductFormFieldId;
    },
): ProductFormField_TextAnswer => {
    return {
        ...input,
        _id: input._id ?? productFormFieldIdFactory(),
        _type: "PRODUCT_FORM_FIELD_ANSWER",
        type: "TEXT",
    };
};
export { productFormTextAnswerFactory, productFormTextAnswerZodObject };
export type {
    ProductFormField_TextAnswer,
    FactoryParams as ProductFormField_TextAnswerFactoryParams,
};
